import { IDepthItem, marketStore } from "entities/market";
import moment from "moment";
import { sircapApi } from "shared/api/sircap";
import { Socket, io } from "socket.io-client";
import { createStore } from "zustand";


interface IOStore {
  socket: Socket | null;
  connected: boolean;
  list: Record<
    string,
    null | {
      bid: number;
      ask: number;
      last_price?: number;
      bid_yield?: number;
      ask_yield?: number;
    }
  >;
  chartSubscribes: Map<
    string,
    { handlers: Array<{ id: string; callback: any }> }
  >;
  // Стаканы из Market Data socket по тикеру (EXCHANGE~SYMBOL): AMX и MOEX.
  orderbooks: Record<string, IDepthItem[]>;
  /**
   * Сколько экранов сейчас смотрят на тикер.
   *
   * На бирже подписка живёт на идентификатор сокета, а он у приложения один:
   * `unsubscribe` от одного экрана гасил котировку у всех остальных, кто
   * смотрел ту же бумагу. Пока счётчик не дошёл до нуля, отписку не шлём.
   */
  subscribers: Record<string, number>;

  connect: () => void;
  disconnect: () => void;
  subscribe: (ticker: string) => void;
  unsubscribe: (ticker: string) => void;
  /**
   * Подписка на стакан отдельно от цены.
   *
   * Нужна только для MOEX: там книга — самостоятельный канал, и держать её для
   * каждой бумаги в списке было бы расточительно. У AMX стакан приезжает вместе
   * с котировкой, и эти вызовы для него не нужны.
   */
  subscribeBook: (ticker: string) => void;
  unsubscribeBook: (ticker: string) => void;
}
const ioRTStore = createStore<IOStore>()((set, get) => ({
  socket: null,
  connected: false,
  list: {},
  chartSubscribes: new Map(),
  orderbooks: {},
  subscribers: {},

  connect: () => {
    const oldSocket = get().socket;
    if (oldSocket) oldSocket.disconnect();
    const socket = io(sircapApi.rtBaseUrl!, {
      path: "/socket",
      autoConnect: false,
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1500,
      // Потолок был равен задержке — backoff не работал. Авторизации у этого
      // сокета нет, поэтому обвязка восстановления ему не нужна.
      reconnectionDelayMax: 30000,
    });
    socket.on("connect", () => {
      console.log("RT_SOCKET: Connected");
      socket.on("ticker", (data) => {
        const { ticker, bid, ask, last_price, bid_yield, ask_yield } =
          JSON.parse(data);
        // console.log(moment().format("HH:mm:ss.SSS"), "ticker", ticker, bid, ask, last_price);
        set({
          list: {
            ...get().list,
            [ticker]: { bid, ask, last_price, bid_yield, ask_yield },
          },
        });
        // if (last_price) {
        //   const subscribes = get().chartSubscribes.get(ticker);
        //   let price = +last_price.toFixed(last_price < 2 ? 4 : 2);
        //   subscribes?.handlers?.forEach((h) =>
        //     h.callback({
        //       time: +new Date(),
        //       // Variant A
        //       // open: +bid.toFixed(4),
        //       // high: +(ask * 1.002).toFixed(4),
        //       // low: +(bid * 0.999).toFixed(4),
        //       // close: +ask.toFixed(4),

        //       // Variant B
        //       // open: bid,
        //       // high: ask,
        //       // low: bid,
        //       // close: ask,

        //       // Variant C
        //       open: price,
        //       high: price,
        //       low: price,
        //       close: price,
        //       volume: 0,
        //     })
        //   );
        // }
      });
      socket.on("orderbook", (data) => {
        const { ticker, orderbook } = JSON.parse(data);
        set({
          orderbooks: {
            ...get().orderbooks,
            [ticker]: orderbook,
          },
        });
      });
      set({ connected: true });
    });
    socket.on("disconnect", () => {
      console.log("RT_SOCKET: Disconnected");
      socket.off("ticker");
      socket.off("orderbook");
      set({ connected: false });
    });
    socket.on("connect_error", () => {
      console.log("RT_SOCKET: Connect error");
      socket.off("ticker");
      socket.off("orderbook");
      set({ connected: false });
    });
    set({ socket });
    socket.connect();
  },
  disconnect: () => {
    const socket = get().socket;
    if (socket) {
      socket.disconnect();
      // Счётчик слушателей живёт вместе с соединением: на новом сокете
      // подписок нет, и остаток от прошлого не дал бы отписаться никогда.
      set({ socket: null, connected: false, subscribers: {} });
    }
  },
  /**
   * Подписка на цену.
   *
   * Прошлое значение не стирается. Раньше здесь стояло `list[ticker] = null`,
   * и каждая переподписка — реконнект, открытие экрана покупки, смена статуса
   * биржи — на мгновение откатывала экран к цене из базы, а затем снимок
   * возвращал живую. Клиент видел скачок и не понимал, какая цена настоящая.
   * Снимок приходит на каждый `subscribe`, так что обнулять нечего: цена
   * просто сменится на свежую, когда та приедет.
   */
  subscribe: (ticker: string) => {
    const subscribers = get().subscribers;
    set({
      subscribers: { ...subscribers, [ticker]: (subscribers[ticker] ?? 0) + 1 },
    });
    get().socket?.emit("subscribe", ticker);
  },
  unsubscribe: (ticker: string) => {
    const { [ticker]: current, ...rest } = get().subscribers;
    // Отписка без подписки. Так делает главная: подписывается под условием
    // (`if (!storiesModal)`), а cleanup эффекта отписывается всегда. Раньше
    // лишний вызов был безобиден, теперь он бы снял чужую подписку.
    if (current === undefined) return;

    const left = current - 1;
    if (left > 0) {
      // Бумагу смотрит ещё кто-то: отписка оборвала бы котировку и ему.
      set({ subscribers: { ...rest, [ticker]: left } });
      return;
    }
    set({ subscribers: rest });
    get().socket?.emit("unsubscribe", ticker);
  },
  subscribeBook: (ticker: string) => {
    get().socket?.emit("subscribe-book", ticker);
  },
  unsubscribeBook: (ticker: string) => {
    get().socket?.emit("unsubscribe-book", ticker);
    // Забываем стакан вместе с подпиской: иначе при следующем открытии бумаги
    // на мгновение показался бы стакан прошлой сессии.
    const { [ticker]: _removed, ...rest } = get().orderbooks;
    set({ orderbooks: rest });
  },
}));

export { ioRTStore };
